/**
 * UNS Mapper - Map data sources to factory structure
 * 
 * A clean interface for:
 * 1. Managing data source connections (InfluxDB, PostgreSQL, MySQL)
 * 2. Browsing data source structure (buckets, measurements, fields)
 * 3. Mapping fields to factory areas, lines, and stations
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFactory } from "@/core/hooks/useFactory";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Checkbox } from "@/shared/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Textarea } from "@/shared/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/shared/ui/context-menu";
import {
  Database,
  Factory,
  ChevronDown,
  ChevronRight,
  Layers,
  Cpu,
  Zap,
  Settings,
  Search as SearchIcon,
  MoreHorizontal,
  Pencil,
  Tag,
  Trash2,
  GitBranch,
} from "lucide-react";
import { DataSourceList } from "@/core/components/admin/DataSourceList";
import { DataSourceBrowser } from "@/core/components/admin/DataSourceBrowser";
import { MappingStatsPopover } from "@/core/components/admin/MappingStatsPopover";
import { toast } from "sonner";
import {
  type DataSource,
  fetchDataSourcesByTenant,
} from "@/core/api/data-sources";

// =============================================================================
// Types
// =============================================================================

interface Area {
  id: string;
  name: string;
  description: string | null;
  color: string;
  sort_order: number;
}

interface Line {
  id: string;
  area_id: string;
  name: string;
  description: string | null;
  sort_order: number;
}

// Extended FieldMapping with datapoint details for display
interface FieldMappingWithDatapoint {
  id: string;
  datapoint_id: string;
  factory_id: string;
  area_id: string | null;
  line_id: string | null;
  station_id: string | null;
  is_active: boolean;
  datapoint: {
    id: string;
    display_name: string;
    field_name: string;
    measurement_name: string;
    bucket_name: string;
    description?: string | null;
    unit_id?: string | null;
    unit?: { symbol: string } | null;
    data_source?: {
      name: string;
      source_type?: string | null;
    } | null;
  };
}

interface Station {
  id: string;
  line_id: string;
  name: string;
  description: string | null;
  station_type: string | null;
  sort_order: number;
}

interface UnitOption {
  id: string;
  name: string;
  symbol: string;
}

interface ModuleOption {
  id: string;
  display_name: string;
}

interface ExposedValueDefinition {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  module_id: string;
}

interface ExposedValueMapping {
  id: string;
  field_mapping_id: string;
  exposed_value: ExposedValueDefinition | null;
}

// =============================================================================
// Helpers
// =============================================================================

const loadPersistedState = (factoryId: string): { 
  expandedAreas: Set<string>; 
  expandedLines: Set<string>;
  activeTab: string;
} => {
  try {
    const stored = localStorage.getItem(`uns-mapper-state-${factoryId}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        expandedAreas: new Set(parsed.expandedAreas || []),
        expandedLines: new Set(parsed.expandedLines || []),
        activeTab: parsed.activeTab || "browser",
      };
    }
  } catch (e) {
    console.error("Failed to load UNS Mapper state:", e);
  }
  return { expandedAreas: new Set(), expandedLines: new Set(), activeTab: "browser" };
};

// =============================================================================
// Component
// =============================================================================

const UNSMapper = () => {
  const { selectedFactoryId, selectedFactory } = useFactory();
  const queryClient = useQueryClient();

  // UI state
  const [activeTab, setActiveTab] = useState(() =>
    selectedFactoryId ? loadPersistedState(selectedFactoryId).activeTab : "browser"
  );
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(() =>
    selectedFactoryId ? loadPersistedState(selectedFactoryId).expandedAreas : new Set()
  );
  const [expandedLines, setExpandedLines] = useState<Set<string>>(() =>
    selectedFactoryId ? loadPersistedState(selectedFactoryId).expandedLines : new Set()
  );
  const [selectedSourceId, setSelectedSourceId] = useState<string>("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<FieldMappingWithDatapoint | null>(null);
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editUnitId, setEditUnitId] = useState<string | null>(null);
  const [editExposedValueIds, setEditExposedValueIds] = useState<string[]>([]);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Persist state
  useEffect(() => {
    if (selectedFactoryId) {
      localStorage.setItem(
        `uns-mapper-state-${selectedFactoryId}`,
        JSON.stringify({
          expandedAreas: Array.from(expandedAreas),
          expandedLines: Array.from(expandedLines),
          activeTab,
        })
      );
    }
  }, [expandedAreas, expandedLines, activeTab, selectedFactoryId]);

  // Reset state when factory changes
  useEffect(() => {
    if (selectedFactoryId) {
      const persisted = loadPersistedState(selectedFactoryId);
      setExpandedAreas(persisted.expandedAreas);
      setExpandedLines(persisted.expandedLines);
      setActiveTab(persisted.activeTab);
      setSelectedSourceId("");
    }
  }, [selectedFactoryId]);

  // =============================================================================
  // Queries
  // =============================================================================

  // Fetch data sources using core schema
  const { data: dataSources = [] } = useQuery({
    queryKey: ["data-sources", selectedFactory?.tenant_id],
    queryFn: async () => {
      if (!selectedFactory?.tenant_id) return [];
      return fetchDataSourcesByTenant(selectedFactory.tenant_id);
    },
    enabled: !!selectedFactory?.tenant_id,
  });

  // Fetch areas
  const { data: areas = [] } = useQuery({
    queryKey: ["factory-areas", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema('core')
        .from("factory_areas")
        .select("id, name, description, color, sort_order")
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Area[];
    },
    enabled: !!selectedFactoryId,
  });

  // Fetch lines
  const { data: lines = [] } = useQuery({
    queryKey: ["production-lines", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema('core')
        .from("production_lines")
        .select("id, area_id, name, description, sort_order")
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Line[];
    },
    enabled: !!selectedFactoryId,
  });

  // Fetch stations
  const { data: stations = [] } = useQuery({
    queryKey: ["stations", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema('core')
        .from("stations")
        .select("id, line_id, name, description, station_type, sort_order")
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Station[];
    },
    enabled: !!selectedFactoryId,
  });

  // Fetch field mappings with datapoint details
  const { data: fieldMappings = [] } = useQuery({
    queryKey: ["field-mappings", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema('core')
        .from("field_mappings")
        .select(`
          id,
          datapoint_id,
          factory_id,
          area_id,
          line_id,
          station_id,
          is_active,
          datapoint:datapoints (
            id,
            display_name,
            field_name,
            measurement_name,
            bucket_name,
            description,
            unit_id,
            unit:units (symbol),
            data_source:data_sources (name, source_type)
          )
        `)
        .eq("factory_id", selectedFactoryId)
        .eq("is_active", true);
      if (error) throw error;
      return data as FieldMappingWithDatapoint[];
    },
    enabled: !!selectedFactoryId,
  });

  const { data: units = [] } = useQuery({
    queryKey: ["core", "units"],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from('units')
        .select('id, name, symbol')
        .order('name');
      if (error) throw error;
      return data as UnitOption[];
    },
    enabled: editDialogOpen,
  });

  const { data: modules = [] } = useQuery({
    queryKey: ["core", "module-registry"],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from('module_registry')
        .select('id, display_name')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as ModuleOption[];
    },
    enabled: editDialogOpen,
  });

  const { data: exposedValues = [] } = useQuery({
    queryKey: ["core", "exposed-value-definitions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from('exposed_value_definitions')
        .select('id, name, display_name, description, module_id')
        .order('display_name');
      if (error) throw error;
      return data as ExposedValueDefinition[];
    },
    enabled: editDialogOpen,
  });

  const fieldMappingIds = useMemo(() => fieldMappings.map((mapping) => mapping.id), [fieldMappings]);

  const { data: exposedValueMappings = [] } = useQuery({
    queryKey: [
      "core",
      "datapoint-exposed-value-mappings",
      selectedFactory?.tenant_id,
      fieldMappingIds.join(","),
    ],
    queryFn: async () => {
      if (!selectedFactory?.tenant_id || fieldMappingIds.length === 0) return [];
      const { data, error } = await supabase
        .schema('core')
        .from('datapoint_exposed_value_mapping')
        .select(`
          id,
          field_mapping_id,
          exposed_value:exposed_value_definitions (
            id,
            name,
            display_name,
            description,
            module_id
          )
        `)
        .eq('tenant_id', selectedFactory.tenant_id)
        .in('field_mapping_id', fieldMappingIds);
      if (error) throw error;
      return data as ExposedValueMapping[];
    },
    enabled: !!selectedFactory?.tenant_id && fieldMappingIds.length > 0,
  });

  const exposedValuesByMapping = useMemo(() => {
    const map = new Map<string, ExposedValueDefinition[]>();
    exposedValueMappings.forEach((mapping) => {
      if (!mapping.exposed_value) return;
      const list = map.get(mapping.field_mapping_id) ?? [];
      list.push(mapping.exposed_value);
      map.set(mapping.field_mapping_id, list);
    });
    return map;
  }, [exposedValueMappings]);

  const exposedValuesByModule = useMemo(() => {
    return exposedValues.reduce((acc, value) => {
      const list = acc.get(value.module_id) ?? [];
      list.push(value);
      acc.set(value.module_id, list);
      return acc;
    }, new Map<string, ExposedValueDefinition[]>());
  }, [exposedValues]);

  useEffect(() => {
    if (!editDialogOpen || !editingMapping) return;
    setEditDisplayName(editingMapping.datapoint.display_name ?? "");
    setEditDescription(editingMapping.datapoint.description ?? "");
    setEditUnitId(editingMapping.datapoint.unit_id ?? null);
    setEditExposedValueIds(exposedValuesByMapping.get(editingMapping.id)?.map((value) => value.id) ?? []);
  }, [editDialogOpen, editingMapping, exposedValuesByMapping]);

  // =============================================================================
  // Handlers
  // =============================================================================

  const toggleArea = (areaId: string) => {
    setExpandedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  };

  const toggleLine = (lineId: string) => {
    setExpandedLines((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const getMappingsForElement = (
    type: "factory" | "area" | "line" | "station",
    id: string
  ) => {
    return fieldMappings.filter((m) => {
      if (type === "factory") return !m.area_id && !m.line_id && !m.station_id;
      if (type === "area") return m.area_id === id && !m.line_id && !m.station_id;
      if (type === "line") return m.line_id === id && !m.station_id;
      if (type === "station") return m.station_id === id;
      return false;
    });
  };

  const deleteMapping = useMutation({
    mutationFn: async (mappingId: string) => {
      const { error } = await supabase
        .schema('core')
        .from('field_mappings')
        .delete()
        .eq('id', mappingId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["field-mappings", selectedFactoryId] });
      queryClient.invalidateQueries({
        queryKey: ["core", "datapoint-exposed-value-mappings", selectedFactory?.tenant_id],
      });
      toast.success("Mapping removed");
    },
    onError: (error) => {
      toast.error("Failed to remove mapping");
      console.error(error);
    },
  });

  const openEditMapping = (mapping: FieldMappingWithDatapoint) => {
    setEditingMapping(mapping);
    setEditDialogOpen(true);
  };

  const toggleExposedValue = (valueId: string) => {
    setEditExposedValueIds((prev) =>
      prev.includes(valueId) ? prev.filter((id) => id !== valueId) : [...prev, valueId]
    );
  };

  const handleSaveEdit = async () => {
    if (!editingMapping || !selectedFactory?.tenant_id) return;
    const trimmedName = editDisplayName.trim();
    if (!trimmedName) {
      toast.error("Display name is required");
      return;
    }
    setIsSavingEdit(true);
    try {
      const { error: datapointError } = await supabase
        .schema('core')
        .from('datapoints')
        .update({
          display_name: trimmedName,
          description: editDescription.trim() || null,
          unit_id: editUnitId,
        })
        .eq('id', editingMapping.datapoint_id)
        .eq('tenant_id', selectedFactory.tenant_id);
      if (datapointError) throw datapointError;

      const { error: deleteError } = await supabase
        .schema('core')
        .from('datapoint_exposed_value_mapping')
        .delete()
        .eq('tenant_id', selectedFactory.tenant_id)
        .eq('field_mapping_id', editingMapping.id);
      if (deleteError) throw deleteError;

      if (editExposedValueIds.length > 0) {
        const { error: insertError } = await supabase
          .schema('core')
          .from('datapoint_exposed_value_mapping')
          .insert(
            editExposedValueIds.map((exposedValueId) => ({
              tenant_id: selectedFactory.tenant_id,
              field_mapping_id: editingMapping.id,
              exposed_value_id: exposedValueId,
            }))
          );
        if (insertError) throw insertError;
      }

      toast.success("Mapping updated");
      queryClient.invalidateQueries({ queryKey: ["field-mappings", selectedFactoryId] });
      queryClient.invalidateQueries({
        queryKey: ["core", "datapoint-exposed-value-mappings", selectedFactory?.tenant_id],
      });
      setEditDialogOpen(false);
    } catch (error) {
      toast.error("Failed to update mapping");
      console.error(error);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const renderMappingList = (mappings: FieldMappingWithDatapoint[]) => {
    if (mappings.length === 0) {
      return (
        <div className="text-xs text-muted-foreground">No datapoints mapped yet.</div>
      );
    }

    return (
      <div className="space-y-2">
        {mappings.map((mapping) => {
          const exposedValuesForMapping = exposedValuesByMapping.get(mapping.id) ?? [];
          const dataSourceName = mapping.datapoint.data_source?.name ?? "Unknown source";
          const dataSourceType = mapping.datapoint.data_source?.source_type ?? "";
          return (
            <ContextMenu key={mapping.id}>
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <div className="rounded-lg border bg-background px-4 py-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">
                            {mapping.datapoint.display_name}
                          </span>
                          {mapping.datapoint.unit?.symbol && (
                            <Badge variant="secondary" className="text-xs">
                              {mapping.datapoint.unit.symbol}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {mapping.datapoint.measurement_name} · {mapping.datapoint.field_name}
                        </p>
                        <div className="flex flex-wrap gap-2 pt-2">
                          {exposedValuesForMapping.length > 0 ? (
                            exposedValuesForMapping.map((value) => (
                              <Badge key={value.id} variant="outline" className="text-[10px]">
                                {value.display_name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              No special values mapped
                            </span>
                          )}
                        </div>
                      </div>
                      <ContextMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground"
                          onClick={(event) => {
                            event.preventDefault();
                            const rect = event.currentTarget.getBoundingClientRect();
                            const menuEvent = new MouseEvent("contextmenu", {
                              bubbles: true,
                              clientX: rect.left,
                              clientY: rect.bottom,
                            });
                            event.currentTarget.dispatchEvent(menuEvent);
                          }}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </ContextMenuTrigger>
                    </div>
                  </div>
                </div>
                <ContextMenuContent className="w-56">
                  <ContextMenuLabel className="text-xs text-muted-foreground">
                    {dataSourceName}{dataSourceType ? ` · ${dataSourceType}` : ""}
                  </ContextMenuLabel>
                  <ContextMenuSeparator />
                  <ContextMenuItem onSelect={() => openEditMapping(mapping)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit mapping
                  </ContextMenuItem>
                  <ContextMenuItem onSelect={() => openEditMapping(mapping)}>
                    <Tag className="mr-2 h-4 w-4" />
                    Manage special values
                  </ContextMenuItem>
                  <ContextMenuSeparator />
                  <ContextMenuItem
                    onSelect={() => deleteMapping.mutate(mapping.id)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Remove mapping
                  </ContextMenuItem>
                </ContextMenuContent>
              </div>
            </ContextMenu>
          );
        })}
      </div>
    );
  };

  // =============================================================================
  // Render
  // =============================================================================

  if (!selectedFactoryId || !selectedFactory) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Card className="p-8 text-center">
          <Factory className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">No Factory Selected</h2>
          <p className="text-muted-foreground">
            Please select a factory to configure UNS mappings
          </p>
        </Card>
      </div>
    );
  }

  const factoryMappings = getMappingsForElement("factory", selectedFactoryId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">UNS Mapper</h1>
          <p className="text-muted-foreground">
            Map data sources to your factory structure using Unified Namespace
          </p>
        </div>
        <MappingStatsPopover
          factoryId={selectedFactoryId}
          tenantId={selectedFactory.tenant_id}
        />
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Data Sources & Browser */}
        <Card className="flex flex-col min-h-[650px]">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Data Sources
            </CardTitle>
            <CardDescription>
              Manage connections and browse data structure
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col min-h-0">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="flex-1 flex flex-col"
            >
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="browser" className="gap-2">
                  <SearchIcon className="h-4 w-4" />
                  Browser
                </TabsTrigger>
                <TabsTrigger value="sources" className="gap-2">
                  <Settings className="h-4 w-4" />
                  Sources
                  {dataSources.length > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs">
                      {dataSources.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="browser"
                className="flex-1 mt-0 min-h-0 h-full data-[state=inactive]:hidden"
              >
                <DataSourceBrowser
                  dataSources={dataSources as DataSource[]}
                  factoryId={selectedFactoryId}
                  selectedSourceId={selectedSourceId}
                  onSourceChange={setSelectedSourceId}
                />
              </TabsContent>

              <TabsContent
                value="sources"
                className="flex-1 mt-0 min-h-0 h-full data-[state=inactive]:hidden"
              >
                <DataSourceList
                  dataSources={dataSources as DataSource[]}
                  tenantId={selectedFactory.tenant_id}
                  factoryId={selectedFactoryId}
                  onSelectSource={(id) => {
                    setSelectedSourceId(id);
                    setActiveTab("browser");
                  }}
                  selectedSourceId={selectedSourceId}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Right Panel - Factory Structure */}
        <Card className="flex flex-col h-[650px]">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Factory className="h-5 w-5" />
              Factory Structure
            </CardTitle>
            <CardDescription>Review mapped datapoints by location</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0">
            <ScrollArea className="h-full pr-2">
              {/* Factory Level */}
              <div className="rounded-lg border bg-background mb-4">
                <button
                  type="button"
                  onClick={() => toggleArea("factory-root")}
                  className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/40 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Factory className="h-5 w-5 text-primary" />
                    <div>
                      <span className="font-semibold">{selectedFactory.name}</span>
                      <p className="text-xs text-muted-foreground">Root Level</p>
                    </div>
                  </div>
                  {expandedAreas.has("factory-root") ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
                {expandedAreas.has("factory-root") && (
                  <div className="border-t p-3 bg-background">
                    {renderMappingList(factoryMappings)}
                  </div>
                )}
              </div>

              {/* Areas, Lines, Stations */}
              {areas.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Layers className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No factory structure configured</p>
                  <p className="text-sm">
                    Add areas, lines, and stations in Factory Structure
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {areas.map((area) => {
                    const areaLines = lines.filter((l) => l.area_id === area.id);
                    const isAreaExpanded = expandedAreas.has(area.id);
                    const areaMappings = getMappingsForElement("area", area.id);

                    return (
                      <div key={area.id} className="relative rounded-lg border bg-background">
                        <span
                          className="absolute left-4 inset-y-4 w-px bg-border/60"
                          aria-hidden
                        />
                        <button
                          type="button"
                          onClick={() => toggleArea(area.id)}
                          className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                        >
                          <div
                            className="h-10 w-1 rounded-full"
                            style={{ backgroundColor: area.color || "var(--primary)" }}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{area.name}</span>
                              <Badge variant="secondary" className="text-xs">
                                {areaLines.length} line
                                {areaLines.length !== 1 ? "s" : ""}
                              </Badge>
                              {areaMappings.length > 0 && (
                                <Badge
                                  variant="outline"
                                  className="text-primary border-primary/30 gap-1 text-xs"
                                >
                                  <Zap className="h-3 w-3" />
                                  {areaMappings.length}
                                </Badge>
                              )}
                            </div>
                            {area.description && (
                              <span className="text-xs text-muted-foreground">
                                {area.description}
                              </span>
                            )}
                          </div>
                          {isAreaExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>

                        {isAreaExpanded && (
                          <div className="border-t px-6 pb-4 pt-3">
                            <div className="relative pl-6">
                              <span className="absolute left-2 inset-y-3 w-px bg-border/50" />
                              <div className="space-y-3">
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-2">
                                    Area mappings
                                  </p>
                                  {renderMappingList(areaMappings)}
                                </div>

                                <div className="space-y-3">
                                  {areaLines.length === 0 ? (
                                    <div className="p-4 text-sm text-muted-foreground text-center">
                                      No production lines
                                    </div>
                                  ) : (
                                    areaLines.map((line) => {
                                      const lineStations = stations.filter(
                                        (s) => s.line_id === line.id
                                      );
                                      const isLineExpanded = expandedLines.has(line.id);
                                      const lineMappings = getMappingsForElement("line", line.id);

                                      return (
                                        <div key={line.id} className="relative rounded-lg border bg-muted/20">
                                          <span className="absolute left-6 inset-y-4 w-px bg-border/50" />
                                          <button
                                            type="button"
                                            onClick={() => toggleLine(line.id)}
                                            className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-muted/30 transition-colors"
                                          >
                                            <GitBranch className="h-4 w-4 text-muted-foreground" />
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium">{line.name}</span>
                                                <Badge variant="outline" className="text-xs">
                                                  {lineStations.length} station
                                                  {lineStations.length !== 1 ? "s" : ""}
                                                </Badge>
                                                {lineMappings.length > 0 && (
                                                  <Badge
                                                    variant="outline"
                                                    className="text-primary border-primary/30 gap-1 text-xs"
                                                  >
                                                    <Zap className="h-3 w-3" />
                                                    {lineMappings.length}
                                                  </Badge>
                                                )}
                                              </div>
                                              {line.description && (
                                                <span className="text-xs text-muted-foreground">
                                                  {line.description}
                                                </span>
                                              )}
                                            </div>
                                            {isLineExpanded ? (
                                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                            ) : (
                                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                            )}
                                          </button>

                                          {isLineExpanded && (
                                              <div className="border-t px-6 pb-4 pt-3">
                                                <div className="relative pl-6 space-y-3">
                                                  <span className="absolute left-2 inset-y-3 w-px bg-border/40" />
                                                <div>
                                                  <p className="text-xs font-medium text-muted-foreground mb-2">
                                                    Line mappings
                                                  </p>
                                                  {renderMappingList(lineMappings)}
                                                </div>
                                                <div className="space-y-3">
                                                  {lineStations.length === 0 ? (
                                                    <div className="text-xs text-muted-foreground">
                                                      No stations configured.
                                                    </div>
                                                  ) : (
                                                    lineStations.map((station) => {
                                                      const stationMappings = getMappingsForElement(
                                                        "station",
                                                        station.id
                                                      );

                                                      return (
                                                        <div
                                                          key={station.id}
                                                          className="rounded-lg border bg-background"
                                                        >
                                                          <div className="flex items-center gap-3 px-4 py-3">
                                                            <Cpu className="h-4 w-4 text-muted-foreground" />
                                                            <div className="flex-1">
                                                              <div className="flex items-center gap-2">
                                                                <span className="text-sm font-medium">
                                                                  {station.name}
                                                                </span>
                                                                {stationMappings.length > 0 && (
                                                                  <Badge
                                                                    variant="outline"
                                                                    className="text-primary border-primary/30 gap-1 text-xs"
                                                                  >
                                                                    <Zap className="h-3 w-3" />
                                                                    {stationMappings.length}
                                                                  </Badge>
                                                                )}
                                                              </div>
                                                              {station.description && (
                                                                <span className="text-xs text-muted-foreground">
                                                                  {station.description}
                                                                </span>
                                                              )}
                                                            </div>
                                                          </div>
                                                          <div className="border-t px-4 py-3">
                                                            {renderMappingList(stationMappings)}
                                                          </div>
                                                        </div>
                                                      );
                                                    })
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) setEditingMapping(null);
        }}
      >
        <DialogContent className="max-w-xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Edit mapping</DialogTitle>
            <DialogDescription>
              Update datapoint details and special value mappings.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-2">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Display name</Label>
                <Input
                  value={editDisplayName}
                  onChange={(event) => setEditDisplayName(event.target.value)}
                  placeholder="Datapoint display name"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editDescription}
                  onChange={(event) => setEditDescription(event.target.value)}
                  placeholder="Optional description"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={editUnitId ?? "none"} onValueChange={(value) => setEditUnitId(value === "none" ? null : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    <SelectItem value="none">No unit</SelectItem>
                    {units.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.symbol} · {unit.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Special mapped values</Label>
                <div className="rounded-lg border p-3">
                  <ScrollArea className="h-72 pr-2">
                    <div className="space-y-3">
                      {modules.map((module) => {
                        const moduleValues = exposedValuesByModule.get(module.id) ?? [];
                        if (moduleValues.length === 0) return null;
                        return (
                          <div key={module.id} className="space-y-2">
                            <p className="text-xs font-semibold text-muted-foreground">
                              {module.display_name}
                            </p>
                            <div className="space-y-2">
                              {moduleValues.map((value) => (
                                <label
                                  key={value.id}
                                  className="flex items-start gap-2 text-sm"
                                >
                                  <Checkbox
                                    checked={editExposedValueIds.includes(value.id)}
                                    onCheckedChange={() => toggleExposedValue(value.id)}
                                    className="mt-1"
                                  />
                                  <span>
                                    <span className="font-medium">{value.display_name}</span>
                                    <span className="block text-xs text-muted-foreground">
                                      {value.description ?? value.name}
                                    </span>
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {modules.length === 0 && (
                        <div className="text-xs text-muted-foreground">No exposed values available.</div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSavingEdit}>
              {isSavingEdit ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UNSMapper;
